/* JFCRX-6733 (QA 2026-10-30). Column widths for the "Variable Widths" PDF
 * layout, extracted from PDFReportViewer.js so the arithmetic can be unit
 * tested without rendering a PDF.
 *
 * THE DEFECT THIS REPLACES. The old code sized each column from its longest
 * value, then scaled every column by the same factor so the total fit the
 * page. Thirteen columns of ordinary Production values already exceed the
 * page, so on every real export that factor was below one -- 0.66 to 0.87
 * across the six accounts on Cluster-DEV -- and every column shrank by it,
 * including columns whose content cannot wrap. react-pdf does not clip a
 * Text wider than its box, so an 11-digit NDC simply ran into the Drug
 * Primary Name column beside it. Which rows caused the shrink did not have
 * to be on the page being looked at: the longest value anywhere in the
 * report sets the factor for all of it.
 *
 * TWO RULES NOW HOLD.
 *
 *   1. A column is never narrower than the widest piece react-pdf could be
 *      forced to leave on one line: for each token in its cells and heading
 *      words, the whole token when it has no hyphenation point (any number,
 *      "Strength", "AZITHROMYCIN"), else its widest hyphenation chunk with
 *      its hyphen ("Quan-" for "Quantity"). When the page is too narrow the
 *      shortfall comes out of columns that have slack to wrap, not out of
 *      columns that have none. Only when even those floors exceed the page
 *      are they scaled down together, because then no layout can avoid
 *      overlap at that font size.
 *
 *      How react-pdf breaks a lone word matters here. Its Knuth-Plass pass
 *      cannot accept a line with no space in it, so a heading or cell that
 *      must break inside a word falls through to textkit's best-fit pass,
 *      which packs hyphenation chunks while their widths alone fit the box
 *      and only then draws the hyphen. Such a line can extend past its box
 *      by up to one hyphen ("Quanti-" in a box that fits exactly "Quanti").
 *      The floor guarantees every chunk fits, which bounds that overhang to
 *      a hyphen, and CELL_PADDING_RIGHT is a hyphen plus MIN_COLUMN_GAP
 *      wide, so even a line that overhangs by a whole hyphen stays a
 *      visible gap clear of the next column. With the old 5pt padding the
 *      three narrowest Production headings ran together by exactly this
 *      mechanism (live on Cluster-DEV, 2026-11-02; reproduced in a Node
 *      render at the same widths, 0.16pt apart).
 *
 *      Floors are capped at MAX_CELL_WIDTH, like preferred widths. A single
 *      unbreakable token wider than that (a long ID, an email address, a
 *      URL) therefore still runs into its neighbour, as it always did. The
 *      alternative, letting one such token claim its full width, would
 *      squeeze every other column, or tip the whole report into the
 *      scale-everything fallback, for the sake of one cell.
 *
 *   2. Text is measured, not guessed. Widths come from Poppins' own advance
 *      widths at the actual font size and weight. The old estimate was a
 *      flat 6.4px per character regardless of font size or character: about
 *      right for an uppercase word at 10pt, a quarter too wide at 8pt, and
 *      far too wide for a "1" or a ".". Measuring matters because the floors
 *      are hard constraints: an estimate that errs wide pushes a 13-column
 *      report into the scale-everything fallback that this change exists to
 *      avoid.
 *
 * WHAT "A TOKEN" IS. The same thing it is to react-pdf's textkit: a run of
 * characters between ASCII spaces (textkit splits words on /[ ]+/ and never
 * breaks inside one). A non-breaking space, a tab or a carriage return is
 * part of the token, not a break. Only "\n" starts a new line.
 *
 * Hyphenation is modelled with the same `hyphen` en-US patterns and default
 * options textkit uses for its word hyphenation (textkit constructs
 * `hyphen(patterns)` with no options; `hyphenateSync` from "hyphen/en-us" is
 * that same construction), so a floor is exactly the narrowest line the
 * renderer can be made to draw. `hyphen` is therefore a direct dependency
 * of the client (package.json), pinned to the exact version the lockfile
 * had already resolved for textkit. The pin matters: a newer `hyphen` is a
 * renderer change as well, because textkit loads the same hoisted copy, and
 * 1.14.1 breaks a handful of English dictionary words at different points
 * from 1.10.6. Upgrading it is a deliberate decision, not a side effect.
 *
 * SUM AND AVERAGE ROWS (JFCRX-7652). The documents draw a Sum row and an
 * Average row under the table on download, at sumAndAvgFontSize (two points
 * larger than the data) with these same widths. They are measured here as
 * `totalsRows`, at their own font size, from the same formatter that draws
 * them (formatPdfTotal in reportExport.js), so what is measured is what is
 * drawn. Each total is floored at its WHOLE width, not at a hyphenation
 * chunk: a cell whose text room holds the whole token never breaks it, so it
 * draws no hyphen and cannot overhang. That is why CELL_PADDING_RIGHT, sized
 * for a hyphen at 10pt, is still enough for totals at 12pt (Large Font; 10pt
 * at Small) (PR #1052 review). The same two limits as for data cells apply:
 * a floor is capped at MAX_CELL_WIDTH (a total of about 26 digits), and when
 * even the floors exceed the page they are scaled down together, so then a
 * total can be squeezed like any other cell.
 */
import { hyphenateSync } from "hyphen/en-us";

/** Landscape Letter: react-pdf's LETTER is 612 x 792pt. */
export const PDF_PAGE_WIDTH = 792;

/** `padding` on the Page in PDFReportDocument.js, which imports this value. */
export const PDF_PAGE_PADDING = 20;

/* Usable width of the page inside its padding. The widths returned below sum
 * to exactly this, and they must: react-pdf gives every node flexShrink 1,
 * so a row whose children sum to more than the page shrinks each of them
 * proportionally, and the floors would no longer be floors. The old code
 * fit its columns to 758, 6pt too wide, which took about 0.8% off every
 * column. */
export const PDF_PAGE_CONTENT_WIDTH = PDF_PAGE_WIDTH - 2 * PDF_PAGE_PADDING;

/** A single cell never asks for more than this; longer values wrap. */
export const MAX_CELL_WIDTH = 200;

/** The least horizontal space that must separate one column's text from the next. */
export const MIN_COLUMN_GAP = 2;

/* paddingRight on every cell Text in PDFReportDocument.js, which imports it.
 * A best-fit line may overhang its box by one hyphen (Rule 1), so this is a
 * hyphen at the largest cell font plus MIN_COLUMN_GAP: Poppins Medium "-" is
 * 5.85pt at 10pt, Regular 5.51pt. The test suite asserts the inequality.
 * Totals render 2pt larger (12pt at Large Font) but are floored whole, so they
 * do not break and do not overhang, outside the two limits stated above. */
export const CELL_PADDING_RIGHT = 8;

/** Rounding slack added to every floor; a preferred width is never below its floor. */
export const FLOOR_MARGIN = 1;

/* Poppins advance widths in 1/1000 em for U+0020 (space) through U+007E (~),
 * read from client/src/fonts with fontkit (unitsPerEm 1000). Poppins has no
 * kern feature, so fontkit's layout() of a string equals the plain sum.
 * Data cells render at the default weight, which registerFonts.js maps to
 * Poppins-Regular; headings render at fontWeight 500, which it maps to
 * Poppins-Medium, whose glyphs run up to 15% wider ("i", "l", "1", "."). */
const ADVANCE = {
  regular: [
    267, 298, 292, 840, 622, 759, 739, 159, 454, 454, 486, 683, 198, 551, 210,
    476, 628, 320, 575, 589, 629, 628, 635, 546, 631, 630, 213, 264, 555, 723,
    539, 524, 1013, 674, 613, 772, 707, 513, 504, 778, 692, 246, 530, 599, 432,
    861, 703, 786, 579, 788, 608, 587, 541, 675, 676, 976, 621, 584, 541, 423,
    658, 423, 629, 733, 257, 676, 676, 607, 676, 620, 329, 676, 640, 246, 248,
    515, 246, 1030, 640, 640, 676, 676, 373, 522, 364, 640, 561, 820, 479, 563,
    455, 462, 291, 462, 519,
  ],
  medium: [
    260, 321, 323, 872, 650, 794, 761, 172, 492, 492, 504, 714, 225, 585, 241,
    514, 641, 350, 577, 594, 647, 639, 642, 560, 638, 635, 245, 304, 612, 774,
    590, 538, 1025, 697, 630, 773, 709, 524, 515, 773, 705, 264, 564, 633, 444,
    882, 721, 784, 595, 787, 632, 604, 564, 690, 694, 999, 660, 605, 558, 485,
    717, 484, 654, 798, 254, 678, 678, 600, 678, 617, 332, 678, 649, 264, 264,
    554, 264, 1039, 649, 638, 678, 678, 384, 534, 372, 649, 576, 825, 499, 582,
    471, 516, 324, 516, 553,
  ],
};

/** Anything outside printable ASCII: wider than every letter but W and M. */
const FALLBACK_ADVANCE = 800;

/**
 * Rendered width of `text` in Poppins at `fontSize` points.
 * @param {"regular"|"medium"} [weight="regular"] regular for data cells,
 *   medium for headings.
 */
export const estimateTextWidth = (text, fontSize, weight = "regular") => {
  const table = ADVANCE[weight];
  let units = 0;
  for (const ch of text) {
    const cp = ch.codePointAt(0);
    units += cp >= 32 && cp <= 126 ? table[cp - 32] : FALLBACK_ADVANCE;
  }
  return (units / 1000) * fontSize;
};

const SOFT_HYPHEN = "­";

/* `hyphen` keeps its own per-word cache, but every call still runs its text
 * reader over the token first, and a report can hold tens of thousands of
 * cells, most of them repeats (the same facility, drug and NDC over and
 * over). The chunks of a token do not depend on font size or weight, so the
 * split result is cached by token here and the library is not consulted
 * again. Bounded so a pathological report cannot grow it without limit. */
const HYPHENATION_CACHE_LIMIT = 50000;
const hyphenationCache = new Map();

const hyphenationChunks = (token) => {
  let chunks = hyphenationCache.get(token);
  if (!chunks) {
    chunks = hyphenateSync(token, { hyphenChar: SOFT_HYPHEN }).split(
      SOFT_HYPHEN,
    );
    if (hyphenationCache.size >= HYPHENATION_CACHE_LIMIT) {
      hyphenationCache.clear();
    }
    hyphenationCache.set(token, chunks);
  }
  return chunks;
};

/**
 * The widest piece react-pdf could be forced to leave on one line when it
 * hyphenates `token` (textkit's default wordHyphenation: `hyphen` en-US,
 * a "-" drawn at the break). The whole token when it has no break point.
 */
export const unbreakableWidth = (token, fontSize, weight = "regular") => {
  const chunks = hyphenationChunks(token);
  if (chunks.length === 1) return estimateTextWidth(token, fontSize, weight);
  return Math.max(
    ...chunks.map((chunk, k) =>
      estimateTextWidth(
        k < chunks.length - 1 ? `${chunk}-` : chunk,
        fontSize,
        weight,
      ),
    ),
  );
};

const cellText = (val) =>
  typeof val === "string" ? val.substring(0, 750) : String(val ?? "");

/** Tokens as textkit sees them: split on ASCII space only, empties dropped. */
const tokensOf = (line) => line.split(" ").filter((t) => t.length > 0);

const sum = (arr) => arr.reduce((a, b) => a + b, 0);

/**
 * @param {object} args
 * @param {string[]} args.headings  column headings, one per column
 * @param {Array<Array<unknown>>} args.data  every row the PDF will print
 * @param {number} [args.fontSize=10]  headingsAndDataFontSize
 * @param {number} [args.pageWidth=PDF_PAGE_CONTENT_WIDTH]
 * @param {Array<Array<string>>} [args.totalsRows=[]]  the formatted Sum/Average
 *   rows the document will draw (see formatPdfTotal); measured in Regular at
 *   totalsFontSize and floored whole, so that a total does not break
 * @param {number} [args.totalsFontSize=fontSize]  sumAndAvgFontSize
 * @returns {number[]} a width per heading, summing to pageWidth
 */
export const computeVariableColumnWidths = ({
  headings,
  data,
  fontSize = 10,
  pageWidth = PDF_PAGE_CONTENT_WIDTH,
  totalsRows = [],
  totalsFontSize = fontSize,
}) => {
  const count = headings.length;
  if (count === 0) return [];

  const preferred = new Array(count).fill(0);
  const floor = new Array(count).fill(0);

  const lineWidth = (text, weight) =>
    Math.min(estimateTextWidth(text, fontSize, weight), MAX_CELL_WIDTH);
  const tokenFloor = (token, weight) =>
    Math.min(unbreakableWidth(token, fontSize, weight), MAX_CELL_WIDTH);

  // Headings contribute their longest word, as before: they are expected
  // to wrap between words.
  headings.forEach((heading, i) => {
    for (const word of tokensOf(cellText(heading))) {
      preferred[i] = Math.max(preferred[i], lineWidth(word, "medium"));
      floor[i] = Math.max(floor[i], tokenFloor(word, "medium"));
    }
  });

  (data || []).forEach((row) => {
    (row || []).forEach((val, i) => {
      if (i >= count) return;
      for (const line of cellText(val).split("\n")) {
        preferred[i] = Math.max(preferred[i], lineWidth(line, "regular"));
        for (const token of tokensOf(line)) {
          floor[i] = Math.max(floor[i], tokenFloor(token, "regular"));
        }
      }
    });
  });

  // Totals: one unbreakable unit per line, at their own font size. Math.max,
  // never assignment: "--" is drawn in every column without a total and must
  // not lower that column's data floor.
  (totalsRows || []).forEach((row) => {
    (row || []).forEach((val, i) => {
      if (i >= count) return;
      for (const line of cellText(val).split("\n")) {
        const w = Math.min(
          estimateTextWidth(line, totalsFontSize, "regular"),
          MAX_CELL_WIDTH,
        );
        preferred[i] = Math.max(preferred[i], w);
        floor[i] = Math.max(floor[i], w);
      }
    });
  });

  for (let i = 0; i < count; i++) {
    preferred[i] += CELL_PADDING_RIGHT;
    floor[i] += CELL_PADDING_RIGHT + FLOOR_MARGIN;
    if (preferred[i] < floor[i]) preferred[i] = floor[i];
  }

  // Everything fits: spread the spare width proportionally, as before.
  const total = sum(preferred);
  if (total <= pageWidth) {
    const factor = pageWidth / total;
    return preferred.map((w) => w * factor);
  }

  // Not even the floors fit: scale them together. Overlap is unavoidable at
  // this font size; this is the least-bad layout, not a clean one.
  const floorTotal = sum(floor);
  if (floorTotal >= pageWidth) {
    const factor = pageWidth / floorTotal;
    return floor.map((w) => w * factor);
  }

  // Shrink, but pin any column that would drop below its floor and take the
  // shortfall from the columns that still have slack. Each pass can only pin
  // more columns, so this terminates within `count` passes.
  const pinned = new Array(count).fill(false);
  for (;;) {
    const freeWidth = pageWidth - sum(floor.filter((_, i) => pinned[i]));
    const freePreferred = sum(preferred.filter((_, i) => !pinned[i]));
    const factor = freePreferred > 0 ? freeWidth / freePreferred : 0;

    let changed = false;
    for (let i = 0; i < count; i++) {
      if (!pinned[i] && preferred[i] * factor < floor[i]) {
        pinned[i] = true;
        changed = true;
      }
    }
    if (!changed) {
      return preferred.map((w, i) => (pinned[i] ? floor[i] : w * factor));
    }
  }
};
